// file: src/services/conversationServices.js

import request from './requestServices';
import {
  API_CONVERSATION_SET_URL,
  API_CONVERSATION_COMPLETION_URL,
  API_CONVERSATION_LIST_URL,
  API_CONVERSATION_GET_URL,
} from './apiRoutes';
import { fetchSse } from '../utils/sseUtils';

// Helper function to generate a UUID v4
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  }).replace(/-/g, ''); // Remove hyphens to match backend format
}

export async function createConversation(dialogId, conversationId = null) {
  const payload = {
    dialog_id: dialogId,
    name: 'New conversation from UI',
    is_new: true,
    conversation_id: conversationId || generateUUID(),
  };
  
  const { data } = await request(API_CONVERSATION_SET_URL, {
    method: 'POST',
    data: payload,
  });
  return data.data; // => { id, dialog_id, name, message, ... }
}

/**
 * chatCompletion => POST /conversation/completion
 *  - conversation_id
 *  - messages: [{ role, content }]
 *  - If onTokenCallback is provided, we do SSE streaming.
 */
export async function chatCompletion(conversationId, messages, onTokenCallback) {
  const payload = {
    conversation_id: conversationId,
    messages,
    stream: Boolean(onTokenCallback), // Turn on SSE if callback is given
  };

  // --- Non-streaming version ---
  if (!onTokenCallback) {
    try {
      const { data } = await request(API_CONVERSATION_COMPLETION_URL, {
        method: 'POST',
        data: payload,
      });
      // data.data => { answer, reference... }
      return data.data;
    } catch (error) {
      console.error('Chat completion error:', error);
      throw error;
    }
  }

  // --- Streaming version with SSE ---
  try {
    // Use the new SSE utility
    const finalResponse = { answer: '', reference: {} };
    
    // Create a handler for SSE events
    const handleSseEvent = (eventData) => {
      if (eventData.code === 0 || eventData.RetCode === 0) {
        if (eventData.data === true) {
          // Final done signal - this will be handled by fetchSse
          return;
        } else {
          // A partial chunk
          if (eventData.data.answer) {
            finalResponse.answer = eventData.data.answer;
          }
          if (eventData.data.reference) {
            finalResponse.reference = eventData.data.reference;
          }
          // Pass the partial data to the callback
          onTokenCallback(eventData.data);
        }
      } else {
        throw new Error(eventData.retmsg || eventData.message || 'Unknown SSE error');
      }
    };
    
    // Use the fetchSse utility
    const result = await fetchSse(
      API_CONVERSATION_COMPLETION_URL,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      handleSseEvent
    );
    
    // If fetchSse returns a final result, use it
    if (result && result.data !== true) {
      if (result.data && result.data.answer) {
        finalResponse.answer = result.data.answer;
      }
      if (result.data && result.data.reference) {
        finalResponse.reference = result.data.reference;
      }
      
      // One final callback with complete data
      onTokenCallback({
        role: 'assistant',
        content: finalResponse.answer,
        reference: finalResponse.reference,
      });
    }
    
    return finalResponse;
  } catch (error) {
    console.error('Streaming chat completion error:', error);
    throw error;
  }
}

export async function listConversations(dialogId) {
  const { data } = await request(`${API_CONVERSATION_LIST_URL}?dialog_id=${dialogId}`, {
    method: 'GET',
  });
  return data.data; // array of { id, dialog_id, name, message, ... }
}

/**
 * Gets detailed information about a specific conversation.
 * @param {string} conversationId 
 * @param {boolean} [suppressNotFound=false] - If true, returns null on 404 instead of throwing.
 */
export async function getConversation(conversationId, suppressNotFound = false) {
  try {
    // Pass suppressNotFoundLog down to the request function
    const { data } = await request(`${API_CONVERSATION_GET_URL}?conversation_id=${conversationId}`, {
      method: 'GET',
      suppressNotFoundLog: suppressNotFound // Pass the flag here
    });
    return data.data;
  } catch (error) {
    // Check if it's a "Not Found" error and suppress if requested
    const isNotFound = error?.response?.status === 404 || 
                      (typeof error?.message === 'string' && error.message.toLowerCase().includes('not found'));
                      
    if (isNotFound && suppressNotFound) {
      // console.log already handled by requestService if suppressNotFoundLog is true
      // console.log(`[conversationServices] Suppressed not found error for conversation ${conversationId}`);
      return null; // Return null instead of throwing
    }
    // Otherwise, re-throw the error (logging was already done by requestService)
    // console.error(`Error fetching conversation ${conversationId}:`, error);
    throw error; 
  }
}